import PropTypes from 'prop-types';
import React__default from 'react';
import SlickCarousel from 'react-slick';
import warning from 'tiny-warning';
import { useComponentStylesLoaded } from '../../hooks/use-component-styles-loaded.js';
import { usePreventVerticalScroll } from '../../hooks/use-prevent-vertical-scroll.js';
import { usePromoBarDarkMode } from './carousel.hooks.js';
import { filterProps } from '../core/core.utils.js';
import { Icon } from '../icon/icon.component.js';
import { assignRefs } from '../../../core/utilities/ref.utils.js';
import { deprecatedPropValues } from '../core/deprecated-prop-values.js';
import { conditionalPropType } from '../../prop-types/conditional.prop-type.js';
import { renderWithRef } from '../core/render-with-ref.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';
import { useId } from '../../hooks/use-id.js';
import { processStyleProps } from '../../../core/utilities/swan-style.js';
import { className } from '../../../core/utilities/classnames.utils.js';
import { SCREEN_CLASS_MAX_WIDTHS } from '../../../core/constants/tokens-screen.js';
import { VisuallyHidden } from '../visually-hidden/visually-hidden.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const hasDots = (props)=>(props === null || props === void 0 ? void 0 : props.dots) || (props === null || props === void 0 ? void 0 : props.progressIndicator) === 'dots' || (props === null || props === void 0 ? void 0 : props.progressIndicator) === 'dots-inset';
const propTypes = {
    /**
   * The visual style of the Carousel.
   * One of: "standard", "full", "promo-bar".
   */ skin: PropTypes.oneOf([
        'standard',
        'full',
        'promo-bar'
    ]),
    /**
   * Specifies the way that slide-progress is indicated to the user.
   * One of: "none", "dots", "dots-inset", "fraction".
   *
   * 'fraction' option is deprecated - use the 'none' value instead
   */ progressIndicator: deprecatedPropValues(PropTypes.oneOf([
        'none',
        'dots',
        'dots-inset',
        'fraction'
    ]), [
        'fraction'
    ], 'Omit or use the `none` value instead'),
    /**
   * Specifies the alignment of the progress indicator.
   * One of: "left", "center".
   */ progressIndicatorAlignment: PropTypes.oneOf([
        'center',
        'left'
    ]),
    /**
   * Whether to show a peek of the next slide.
   */ peek: PropTypes.bool,
    /**
   * Whether to place gutters between the slides that match the grid's gutters.
   */ gridGutters: PropTypes.bool,
    /**
   * What kind of gutters to place between slides.
   */ gridGuttersVariant: PropTypes.string,
    /**
   * The vertical position of the arrows, as a CSS "top" property.
   */ arrowVerticalPosition: PropTypes.string,
    /**
   * The vertical offset of the arrows, relative to its CSS "top" property.
   */ arrowVerticalOffset: PropTypes.string,
    /**
   * Set accessible text for previous` button.
   */ accessibleTextForPrevious: PropTypes.string.isRequired,
    /**
   * Set accessible text for next button.
   */ accessibleTextForNext: PropTypes.string.isRequired,
    /**
   * Set accessible text for carousel dots.
   */ accessibleTextForDots: conditionalPropType(hasDots, PropTypes.arrayOf(PropTypes.string.isRequired).isRequired, PropTypes.arrayOf(PropTypes.string.isRequired)),
    /**
   * The current slide of the carousel.
   */ currentSlide: PropTypes.number
};
const CarouselButton = ({ onClick, accessibleText, iconType, className, style })=>{
    const id = useId();
    /**
   * Disabling the control button based on the class `slick-disabled` applied by react-slick
   * https://stackoverflow.com/a/67641248
   */ return /*#__PURE__*/ React__default.createElement("button", {
        onClick: onClick,
        disabled: className === null || className === void 0 ? void 0 : className.includes('slick-disabled'),
        className: className,
        style: style,
        "aria-labelledby": id
    }, /*#__PURE__*/ React__default.createElement(VisuallyHidden, {
        id: id
    }, accessibleText), /*#__PURE__*/ React__default.createElement(Icon, {
        alt: "",
        iconType: iconType,
        size: "16p"
    }));
};
const propKeysToRemove = [
    'role',
    'aria-label',
    'aria-labelledby',
    'aria-roledescription',
    ...Object.keys(propTypes)
];
/**
 * a11y requires pause controls if a Carousel auto-plays. We don't currently have pause controls designed, particularly for the Promo Bar.
 * Until we can support a11y by designing and adding proper pause controls, disable autoplay.
 */ const autoplay = false;
const Carousel = renderWithRef('Carousel', propTypes, (props, ref)=>{
    useComponentStylesLoaded('Carousel', SWAN_STYLE_KEY_MAP.carousel);
    const { as, component, skin, progressIndicator, progressIndicatorAlignment, peek, gridGutters, gridGuttersVariant, arrowVerticalPosition, arrowVerticalOffset = '0px', children, // we extract some of the slickCarouselProps in order to add necessary classes
    // the defaults that we set here should match the Slick documentation so that we don't confuse folks
    dots, arrows, variableWidth, centerMode, slidesToShow = 1, centerPadding, // Props for container div
    role, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-roledescription': ariaRoleDescription, accessibleTextForPrevious, accessibleTextForNext, accessibleTextForDots, customPaging, currentSlide, initialSlide = currentSlide, id: customId } = props;
    const [isInitialized, setIsInitialized] = React__default.useState(skin === 'promo-bar');
    const finalSlidesToShow = progressIndicator === 'fraction' ? 1 : slidesToShow;
    const id = useId(customId);
    const placeholderClass = `swan-carousel-placeholder-${id}`;
    const carouselContainerRef = React__default.useRef(null);
    const carouselRef = React__default.useRef();
    const combinedRef = assignRefs(ref, carouselRef);
    const dotsEnabled = dots || progressIndicator !== 'none' || !!customPaging;
    const arrowEnabled = arrows !== null && arrows !== void 0 ? arrows : progressIndicator === 'fraction';
    const { forceDarkMode, beforeChange } = usePromoBarDarkMode(carouselRef, props);
    const finalProps = {
        prevArrow: /*#__PURE__*/ React__default.createElement(CarouselButton, {
            accessibleText: accessibleTextForPrevious,
            iconType: "chevronLeft"
        }),
        nextArrow: /*#__PURE__*/ React__default.createElement(CarouselButton, {
            accessibleText: accessibleTextForNext,
            iconType: "chevronRight"
        }),
        arrows: arrowEnabled,
        centerMode: !!(centerMode || peek),
        dots: dotsEnabled,
        customPaging: dotsEnabled ? customPaging || ((index)=>{
            const content = Array.isArray(accessibleTextForDots) && accessibleTextForDots[index] != undefined ? accessibleTextForDots[index] : index + 1;
            return progressIndicator === 'fraction' ? /*#__PURE__*/ React__default.createElement("span", null, content) : /*#__PURE__*/ React__default.createElement("button", null, content);
        }) : undefined,
        appendDots: dotsEnabled ? (dots)=>{
            return /*#__PURE__*/ React__default.createElement("ul", null, React__default.Children.map(dots, (dot)=>/*#__PURE__*/ React__default.isValidElement(dot) && progressIndicator !== 'fraction' ? /*#__PURE__*/ React__default.cloneElement(dot, {}, /*#__PURE__*/ React__default.cloneElement(dot.props.children, {
                    'aria-pressed': dot.props.className === 'slick-active'
                })) : dot));
        } : undefined,
        beforeChange: beforeChange,
        initialSlide: typeof initialSlide === 'number' ? initialSlide : peek && finalSlidesToShow > 1 ? 1 : 0,
        slidesToShow: finalSlidesToShow,
        autoplay,
        pauseOnHover: autoplay,
        pauseOnFocus: autoplay,
        pauseOnDotsHover: autoplay,
        variableWidth
    };
    const finalPropsXS = {
        arrows,
        autoplay
    };
    if (centerPadding !== undefined) {
        finalProps.centerPadding = centerPadding;
        finalPropsXS.centerPadding = centerPadding;
    }
    if (progressIndicator === 'fraction' && peek) {
        finalProps.centerPadding = '70';
    }
    if (peek) {
        finalPropsXS.centerPadding = '8';
    }
    warning(props.__dangerouslySuppressWarning || !props.autoplay, 'Carousel autoplay is currently disabled, pending future accessibility support');
    warning(dotsEnabled || arrowEnabled, 'At least one of dots or arrow must be enabled for a carousel');
    warning(as === undefined && component === undefined, 'Carousel does not support the `component` or `as` prop because it must render a third-party component');
    const classNameIter = processStyleProps(_object_spread_props(_object_spread({}, props), {
        darkMode: forceDarkMode || props.darkMode
    }));
    const filteredProps = filterProps(props, propKeysToRemove, classNameIter);
    const styleClasses = className(filteredProps.className, 'swan-carousel', {
        [`swan-carousel-skin-${skin}`]: skin !== 'standard',
        'swan-carousel-dots': finalProps.dots,
        'swan-carousel-dots-left': finalProps.dots && progressIndicatorAlignment === 'left',
        'swan-carousel-dots-fraction': progressIndicator === 'fraction',
        'swan-carousel-dots-inset': progressIndicator === 'dots-inset',
        'swan-carousel-hide-arrows': !finalProps.arrows,
        'swan-carousel-variable-width': finalProps.variableWidth,
        'swan-carousel-grid-gutters': gridGutters,
        'swan-carousel-grid-gutters-tight': gridGutters && gridGuttersVariant === 'tight',
        'swan-carousel-placeholder': !isInitialized,
        ['swan-carousel-peek-non-focusable-' + slidesToShow]: !!peek && slidesToShow % 2 === 0,
        [placeholderClass]: !isInitialized
    });
    const containerStyle = {
        '--swan-carousel-arrow-top': arrowVerticalPosition,
        '--swan-carousel-arrow-vertical-offset': arrowVerticalOffset,
        '--swan-internal-carousel-placeholder-slides-to-show': slidesToShow || 1
    };
    usePreventVerticalScroll(carouselContainerRef);
    React__default.useEffect(()=>{
        setIsInitialized(true);
    }, []);
    React__default.useEffect(()=>{
        if (typeof currentSlide === 'number' && carouselRef.current) {
            carouselRef.current.slickGoTo(currentSlide);
        }
    }, [
        currentSlide
    ]);
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "swan-carousel-container",
        ref: carouselContainerRef,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-roledescription": ariaRoleDescription,
        role: role,
        style: containerStyle
    }, isInitialized ? /*#__PURE__*/ React__default.createElement(SlickCarousel, _object_spread(_object_spread_props(_object_spread({
        responsive: [
            {
                breakpoint: SCREEN_CLASS_MAX_WIDTHS.xs,
                settings: finalPropsXS
            }
        ]
    }, filteredProps, finalProps), {
        ref: combinedRef,
        className: styleClasses
    }), {
        children
    })) : /*#__PURE__*/ React__default.createElement("div", {
        className: styleClasses
    }, /*#__PURE__*/ React__default.createElement("style", {
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML: {
            __html: `
                .${placeholderClass} .slick-slide:nth-of-type(${Math.ceil(slidesToShow)}) ~ .slick-slide  {
                  display: none !important;
                }
              `
        }
    }), /*#__PURE__*/ React__default.createElement("div", {
        className: "slick-list"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "slick-track"
    }, React__default.Children.map(children, (slide)=>/*#__PURE__*/ React__default.createElement("div", {
            className: "slick-slide"
        }, /*#__PURE__*/ React__default.createElement("div", null, slide)))))));
});
Carousel.defaultProps = {
    skin: 'standard',
    progressIndicator: 'none',
    progressIndicatorAlignment: 'center',
    peek: false,
    gridGutters: false,
    gridGuttersVariant: 'standard',
    dots: false,
    arrows: true,
    variableWidth: false,
    centerMode: false,
    slidesToShow: 1,
    role: 'region',
    'aria-roledescription': 'carousel',
    arrowVerticalOffset: '0px'
};

export { Carousel };
